'use client';

import { type ReactNode, useMemo } from 'react';

import { DesktopSmall, MobileSmall } from '@wix/wix-ui-icons-common';
import { Columns3, MessageSquare } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { useBiLoggerContext } from '@/components/Contexts/BiLoggerContext';
import { BI_CLICK } from '@/constants/bi';
import { getDeviceLabel } from '@/utils/getDeviceLabel';

import { HamburgerMenu } from './HamburgerMenu';
import { ToolbarSegmentedControl } from './ToolbarSegmentedControl';

import type { WorkbenchView } from '@/components/WorkbenchPage/types';
import type { DevicePreset } from '@/types/feedback';

type Mode = 'browse' | 'comment';

interface ToolbarClientProps {
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  view: WorkbenchView;
  onViewChange: (view: WorkbenchView) => void;
  device: DevicePreset;
  onDeviceChange: (preset: DevicePreset) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  hasUnread?: boolean;
  openCommentsCount?: number;
  isPartner?: boolean;
  metaSiteId?: string;
  targetUrl?: string;
}

const DEVICE_KEYS: DevicePreset[] = ['mobile', 'desktop'];

const DEVICE_ICONS: Partial<Record<DevicePreset, ReactNode>> = {
  mobile: <MobileSmall />,
  desktop: <DesktopSmall />,
};

function buildDeviceItems(t: ReturnType<typeof useTranslations>) {
  return DEVICE_KEYS.map((key) => {
    const label = getDeviceLabel(key, t);
    return {
      key,
      label,
      title: label,
      icon: DEVICE_ICONS[key],
    };
  });
}

export function ToolbarClient({
  mode,
  onModeChange,
  view,
  onViewChange,
  device,
  onDeviceChange,
  sidebarOpen,
  onToggleSidebar,
  hasUnread = false,
  openCommentsCount = 0,
  isPartner = false,
  metaSiteId,
  targetUrl,
}: ToolbarClientProps) {
  const t = useTranslations();
  const { reportClick } = useBiLoggerContext();

  const handleModeChange = (next: Mode) => {
    reportClick({ clickType: BI_CLICK.MODE_TOGGLE, text: next });
    onModeChange(next);
  };

  const handleToggleSidebar = () => {
    reportClick({
      clickType: BI_CLICK.SIDEBAR_TOGGLE,
      text: String(!sidebarOpen),
    });
    onToggleSidebar();
  };

  const modeItems: Array<{ key: Mode; label: string; title: string }> = useMemo(
    () => [
      {
        key: 'comment',
        label: t('get-feedback-toolbar-comment'),
        title: t('get-feedback-toolbar-commentTooltip'),
      },
      {
        key: 'browse',
        label: t('get-feedback-toolbar-browse'),
        title: t('get-feedback-toolbar-browseTooltip'),
      },
    ],
    [t],
  );

  const canvasActive = view === 'canvas';
  const selectedDeviceOrCanvas: DevicePreset | 'canvas' = canvasActive
    ? 'canvas'
    : device;

  const handleViewOrDeviceChange = (key: DevicePreset | 'canvas') => {
    if (key === 'canvas') {
      const next = canvasActive ? 'workbench' : 'canvas';
      reportClick({ clickType: BI_CLICK.VIEW_TOGGLE, text: next });
      onViewChange(next);
    } else {
      reportClick({ clickType: BI_CLICK.DEVICE_SWITCH, text: key });
      if (canvasActive) onViewChange('workbench');
      onDeviceChange(key);
    }
  };

  const deviceAndCanvasItems = useMemo(() => {
    const deviceItems = buildDeviceItems(t);
    return [
      ...deviceItems,
      {
        key: 'canvas' as const,
        label: t('get-feedback-toolbar-canvas'),
        title: t('get-feedback-toolbar-canvasTooltip'),
        icon: <Columns3 className="h-4 w-4" />,
      },
    ];
  }, [t]);

  return (
    <div className="flex w-full items-center">
      {/* Left: menu + mode toggle */}
      <div className="flex flex-1 items-center gap-3">
        {isPartner ? (
          <HamburgerMenu metaSiteId={metaSiteId} targetUrl={targetUrl} />
        ) : (
          <ToolbarSegmentedControl
            items={modeItems}
            value={mode}
            onChange={handleModeChange}
          />
        )}
      </div>

      {/* Center: breakpoints/canvas + zoom */}
      <div className="flex flex-1 items-center justify-center gap-2">
        <ToolbarSegmentedControl
          items={deviceAndCanvasItems}
          value={selectedDeviceOrCanvas}
          onChange={handleViewOrDeviceChange}
          density="compact"
        />
      </div>

      {/* Right: comments toggle */}
      <div className="flex flex-1 items-center justify-end">
        <div className="relative inline-flex">
          <button
            type="button"
            data-hook="toolbar-comments-toggle"
            onClick={handleToggleSidebar}
            aria-pressed={sidebarOpen}
            title={
              sidebarOpen
                ? t('get-feedback-toolbar-hideComments')
                : t('get-feedback-toolbar-showComments')
            }
            className={[
              'flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-medium transition-all border',
              sidebarOpen
                ? 'border-blue-700 bg-[#EEF5FC] text-blue-700'
                : 'border-slate-400 text-blue-950 hover:bg-blue-50 hover:text-blue-950',
            ].join(' ')}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            {t('get-feedback-toolbar-comments')}
            {openCommentsCount > 0 && <span>({openCommentsCount})</span>}
          </button>
          {hasUnread && (
            <span
              data-hook="toolbar-unread-badge"
              className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-blue-700 ring-2 ring-background"
            />
          )}
        </div>
      </div>
    </div>
  );
}
